<ng-container *transloco="let t; read: 'configuration-taxonomy-component'">
  @if (configuration().removed) {
    <div class="configuration-taxonomy__deleted">
      {{ t('been-deleted') }}
    </div>
  } @else {
    <as-split direction="vertical">
      <as-split-area [size]="70" [minSize]="25">
        <as-split direction="horizontal">
          <as-split-area [size]="35" [minSize]="10">
            <div class="configuration-taxonomy__column">
              @if (isLoading.taxonomy) {
                <div class="configuration-taxonomy__loading">
                  <xi-animation [loop]="true" animation="cx_loader"></xi-animation>
                </div>
              }
              <div class="configuration-taxonomy__search-wrapper">
                <app-lex-input
                  placeholder="{{ t('search-for-node') }}"
                  suffixAction="clear"
                  class="configuration-taxonomy__filter"
                  [disabled]="isSaving || !isValid"
                  [value]="searchValue"
                  (valueChange)="onSearchChanged($event)"
                ></app-lex-input>
                @if (searchValue) {
                  <div @searchControlTrigger>
                    <xi-icon-button
                      color="CX"
                      icon="single_arrow_left"
                      [disabled]="!searchMatches.length || isSaving"
                      (onClick)="searchPrevious()"
                    ></xi-icon-button>
                  </div>
                }
                @if (searchValue) {
                  <div @searchControlTrigger>
                    <xi-icon-button
                      color="CX"
                      icon="single_arrow_right"
                      [disabled]="!searchMatches.length || isSaving"
                      (onClick)="searchNext()"
                    ></xi-icon-button>
                  </div>
                }
                <xi-icon-button
                  color="CX"
                  icon="tree_picker_collapse"
                  [disabled]="isSaving || !canCollapse"
                  [tooltip]="{ text: t('collapse-all') }"
                  (onClick)="collapseAll()"
                ></xi-icon-button>
                @if (_canEditItem) {
                  <div class="configuration-taxonomy__add-button">
                    <xi-icon-button
                      color="CX"
                      icon="add_circle"
                      [disabled]="isSaving || !isValid"
                      (onClick)="addRootNode()"
                    ></xi-icon-button>
                  </div>
                }
                @if (_canEditItem) {
                  <div>
                    <xi-icon-button
                      color="CX"
                      icon="trash"
                      [disabled]="isSaving"
                      (onClick)="deleteAllNodes()"
                      [tooltip]="{ text: t('delete-all'), direction: 5 }"
                    ></xi-icon-button>
                  </div>
                }
              </div>
              @if (dataSource?.data?.length === 0) {
                <div class="configuration-taxonomy__empty-selection">
                  {{ t('no-nodes-defined') }}
                </div>
              } @else {
                <div class="configuration-taxonomy__scroll" #itemListScroll>
                  <mat-tree
                    [dataSource]="dataSource"
                    [treeControl]="treeControl"
                    class="configuration-taxonomy__taxonomy-tree"
                    cdkDropList
                  >
                    <mat-tree-node
                      *matTreeNodeDef="let node; when: isElementNode"
                      matTreeNodeToggle
                      cdkDrag
                      [cdkDragData]="node"
                      [cdkDragDisabled]="!_canEditItem"
                      cdkDragBoundary=".mat-tree"
                      (cdkDragStarted)="onNodeDragStarted($event)"
                      (cdkDragMoved)="nodeDragged$.next($event)"
                      (cdkDragDropped)="onNodeDropped($event)"
                      (click)="onTreeNodeClicked(node)"
                      [attr.data-node-uuid]="node.uuid"
                    >
                      <div
                        class="configuration-taxonomy__row configuration-taxonomy__row--target"
                        [class.configuration-taxonomy__row--selected]="selectedElement === node"
                      >
                        <ng-template
                          *ngTemplateOutlet="targetRowLayout; context: { $implicit: node }"
                        >
                        </ng-template>
                      </div>
                    </mat-tree-node>
                    <mat-nested-tree-node
                      *matTreeNodeDef="let node; when: displayExpandable"
                      cdkDropList
                      [class.configuration-taxonomy__row--new]="isNodeNew(node)"
                    >
                      <div
                        cdkDrag
                        [cdkDragData]="node"
                        [cdkDragDisabled]="!_canEditItem"
                        cdkDragBoundary=".mat-tree"
                        (cdkDragStarted)="onNodeDragStarted($event)"
                        (cdkDragMoved)="nodeDragged$.next($event)"
                        (cdkDragDropped)="onNodeDropped($event)"
                        (click)="onTreeNodeClicked(node)"
                        [attr.data-node-uuid]="node.uuid"
                        class="configuration-taxonomy__row configuration-taxonomy__row--taxonomy"
                        [class.configuration-taxonomy__row--expanded]="treeControl.isExpanded(node)"
                        [class.configuration-taxonomy__row--selected]="selectedNode === node"
                        [class.configuration-taxonomy__row--error]="itemName.invalid"
                      >
                        <button
                          mat-icon-button
                          [attr.aria-label]="t('toggle-name', { name: node.name })"
                          class="configuration-taxonomy__row-expand-button"
                          (click)="toggleAndSelectNode($event, node)"
                        >
                          <mat-icon class="mat-icon-rtl-mirror">
                            {{ treeControl.isExpanded(node) ? 'expand_more' : 'chevron_right' }}
                          </mat-icon>
                        </button>
                        <ng-template
                          *ngTemplateOutlet="elementRowLayout; context: { $implicit: node }"
                        >
                        </ng-template>
                      </div>
                      @if (treeControl.isExpanded(node)) {
                        <div
                          [class.configuration-taxonomy__row--collapsed]="
                            !treeControl.isExpanded(node)
                          "
                          role="group"
                        >
                          <ng-container matTreeNodeOutlet></ng-container>
                        </div>
                      }
                    </mat-nested-tree-node>
                  </mat-tree>
                  <ng-template #targetRowLayout let-item>
                    <div class="configuration-taxonomy__target-name">
                      <div
                        class="configuration-taxonomy__target-type"
                        [ngClass]="'configuration-taxonomy__target-type--' + item.target_type"
                      >
                        {{ getTargetPrefixFromElement(item) }}
                      </div>
                      <ng-container
                        *appMiddleEllipsize="
                          getTargetName(item) || t('target-missing');
                          tooltip: getTargetName(item) || t('target-missing-tooltip')
                        "
                      ></ng-container>
                    </div>
                    <div class="configuration-taxonomy__row-action">
                      <xi-icon-button
                        color="CX"
                        icon="cancel"
                        [disabled]="!_canEditItem"
                        (onClick)="deleteTarget(item)"
                      ></xi-icon-button>
                    </div>
                  </ng-template>
                  <ng-template #elementRowLayout let-item>
                    <div class="configuration-taxonomy__item-name">
                      @if (selectedNode === item) {
                        <app-inline-edit
                          appEditOnEnter
                          [control]="itemName"
                          class="configuration-taxonomy__item-name-editor"
                          (update)="sortSiblingNodes(item)"
                        >
                          <ng-template appViewMode>
                            <div class="configuration-taxonomy__item-name-editor--view">
                              @if (!selectedNode.name) {
                                <span class="configuration-taxonomy__item-name-editor--placeholder">
                                  {{ t('enter-name') }}
                                </span>
                              }
                              <ng-container *appMiddleEllipsize="selectedNode.name"></ng-container>
                            </div>
                          </ng-template>
                          <ng-template appEditMode>
                            <mat-form-field class="configuration-taxonomy__item-name-editor--edit">
                              <input
                                matInput
                                appAutoFocus
                                [autoFocusTimeout]="100"
                                [formControl]="itemName"
                              />
                              @if (itemName.hasError('minLength')) {
                                <mat-error>
                                  {{
                                    t('name-too-short', {
                                      minLength: itemName.errors.minLength.minLength,
                                    })
                                  }}
                                </mat-error>
                              }
                              @if (itemName.hasError('maxLength')) {
                                <mat-error>
                                  {{
                                    t('name-too-long', {
                                      maxLength: itemName.errors.maxLength.maxLength,
                                    })
                                  }}
                                </mat-error>
                              }
                              @if (itemName.hasError('nameAlreadyExists')) {
                                <mat-error>
                                  {{ t('duplicate-name') }}
                                </mat-error>
                              }
                            </mat-form-field>
                          </ng-template>
                        </app-inline-edit>
                      } @else {
                        <ng-container *appMiddleEllipsize="item.name"></ng-container>
                      }
                    </div>
                    @if (_canEditItem) {
                      <div class="configuration-taxonomy__row-action">
                        <xi-icon-button
                          icon="add_circle"
                          color="CX"
                          [disabled]="isSaving || !isValid"
                          (onClick)="addChildNode(item)"
                        ></xi-icon-button>
                      </div>
                    }
                    @if (_canEditItem) {
                      <div class="configuration-taxonomy__row-action">
                        <xi-icon-button
                          icon="trash"
                          color="CX"
                          (onClick)="deleteNode(item)"
                        ></xi-icon-button>
                      </div>
                    }
                    @if (!editable) {
                      <div class="configuration-taxonomy__row-action">
                        <xi-icon icon="password_filled"></xi-icon>
                      </div>
                    }
                  </ng-template>
                </div>
              }
              @if (currentTargetContainments.length > 1) {
                <div class="configuration-taxonomy__target-containment-wrapper">
                  <app-lex-button size="medium" (click)="nextTargetOccurrence()">
                    {{ t('next-occurrence') }}
                  </app-lex-button>
                  <xi-icon-button
                    icon="cancel"
                    color="CX"
                    (onClick)="closeTargetOccurrences()"
                  ></xi-icon-button>
                </div>
              }
            </div>
          </as-split-area>
          <as-split-area [size]="65" [minSize]="30">
            <div
              class="configuration-taxonomy__template-wrapper configuration-taxonomy__template-top-right"
            >
              <div class="configuration-taxonomy__column configuration-taxonomy__column--editor">
                @if (isLoading.target) {
                  <div class="configuration-taxonomy__loading">
                    <xi-animation [loop]="true" animation="cx_loader"></xi-animation>
                  </div>
                }
                <div class="configuration-taxonomy__editor-search-wrapper">
                  <app-lex-input
                    suffixAction="clear"
                    [disabled]="isSaving"
                    [validators]="[targetSearchLengthValidator]"
                    [validatorMessages]="{ notfound: t('no-results') }"
                    [value]="targetSearchValue"
                    (valueChange)="onTargetSearchChanged($event)"
                    class="configuration-taxonomy__editor-search"
                  >
                    {{ t('search-for-target') }}
                  </app-lex-input>
                  <app-lex-select
                    [options]="targetSearchValues"
                    [validators]="[targetSearchLengthValidator]"
                    [value]="targetSearchType"
                    [valueAccessor]="targetSearchValueAccessor"
                    (valueChange)="onTargetSearchTypeChanged($event)"
                  ></app-lex-select>
                  @if (targetSearchValue) {
                    <div @searchControlTrigger class="configuration-taxonomy__left-arrow">
                      <xi-icon-button
                        icon="single_arrow_left"
                        color="CX"
                        [disabled]="!filteredTargets.length || isSaving"
                        (onClick)="targetSearchPrevious()"
                      ></xi-icon-button>
                    </div>
                  }
                  @if (targetSearchValue) {
                    <div @searchControlTrigger>
                      <xi-icon-button
                        icon="single_arrow_right"
                        color="CX"
                        [disabled]="!filteredTargets.length || isSaving"
                        (onClick)="targetSearchNext()"
                      ></xi-icon-button>
                    </div>
                  }
                  <div
                    class="configuration-taxonomy__target-kind-button"
                    #targetSearchKindPopover="ngbPopover"
                    [ngbPopover]="tTargetKind"
                    popoverClass="info-popover configuration-taxonomy__target-search-kind-popover"
                    container="body"
                    placement="left-top"
                    [autoClose]="'outside'"
                  >
                    <span [matTooltip]="targetSearchKind.label">{{
                      targetSearchKind.shortLabel
                    }}</span>
                  </div>
                  <div
                    class="configuration-taxonomy__sort-button"
                    #targetSortPopover="ngbPopover"
                    [ngbPopover]="tTargetSort"
                    popoverClass="info-popover configuration-taxonomy__target-sort-popover"
                    container="body"
                    placement="left-top"
                    [autoClose]="'outside'"
                  >
                    <xi-icon-button
                      icon="sort_order_desc"
                      color="CX"
                      [hasAnimation]="false"
                      [tooltip]="{ text: targetSort.label }"
                    ></xi-icon-button>
                  </div>
                  <xi-icon-button
                    icon="cancel"
                    color="CX"
                    (onClick)="clearTargetSearchFilters()"
                  ></xi-icon-button>
                </div>
                <div class="configuration-taxonomy__editor-target-list">
                  @if (!filteredTargets?.length) {
                    <div></div>
                    <div class="configuration-taxonomy__empty-selection">
                      {{ t('no-search-results') }}
                    </div>
                  } @else {
                    <cdk-virtual-scroll-viewport class="virtual-viewport" itemSize="40">
                      <mat-action-list dense>
                        <button
                          *cdkVirtualFor="let target of filteredTargets"
                          mat-list-item
                          [class.configuration-taxonomy__row--selected]="selectedTarget === target"
                          (click)="onTargetRowClicked(target)"
                        >
                          <div class="configuration-taxonomy__target-name">
                            @if (_canEditItem && selectedTarget === target) {
                              <div
                                class="configuration-taxonomy__target-add"
                                [matTooltip]="addTargetToNodeButtonTooltip"
                              >
                                <xi-icon-button
                                  icon="circle_arrow_left"
                                  color="CX"
                                  [disabled]="!canAddTargetToNode"
                                  (onClick)="addTargetToNode()"
                                ></xi-icon-button>
                              </div>
                            }
                            <div
                              class="configuration-taxonomy__target-type"
                              [ngClass]="
                                'configuration-taxonomy__target-type--' + getTargetPrefixCSS(target)
                              "
                            >
                              {{ getTargetPrefix(target) }}
                            </div>
                            {{ target.normalized || target.name }}
                          </div>
                          @if (targetSort.value === 'modification_date') {
                            <div class="configuration-taxonomy__target-updated">
                              {{ target.modification_date | lexDate }}
                            </div>
                          }
                          <div
                            class="configuration-taxonomy__target-containment"
                            (click)="showTargetOccurrences($event, target)"
                          >
                            @if (targetContainments.has(target.id)) {
                              <span>
                                {{
                                  t('times-in-tree', {
                                    length: targetContainments.get(target.id).length,
                                  })
                                }}
                              </span>
                            }
                          </div>
                        </button>
                      </mat-action-list>
                    </cdk-virtual-scroll-viewport>
                  }
                </div>
              </div>
            </div>
          </as-split-area>
        </as-split>
      </as-split-area>
      <as-split-area [size]="30" [minSize]="15">
        <div
          class="configuration-taxonomy__template-wrapper configuration-taxonomy__template-bottom"
        >
          <div class="configuration-taxonomy__editor-header">
            @if (targetQuery && selectedTarget) {
              <div
                class="configuration-taxonomy__target-type"
                [ngClass]="
                  'configuration-taxonomy__target-type--' + getTargetPrefixCSS(selectedTarget)
                "
              >
                {{ getTargetPrefix(selectedTarget) }}
              </div>
              <div class="configuration-taxonomy__editor-header-itemname">
                {{ selectedTarget.normalized || selectedTarget.name }}
              </div>
              <div>({{ getTargetType(selectedTarget).label }})</div>
            }
          </div>
          <div class="configuration-taxonomy__editor-query">
            @if (targets?.length) {
              <code-editor
                [value]="targetQuery"
                [lineWrapping]="true"
                [readonly]="true"
                [placeholder]="t('analytic-item-placeholder')"
                [setup]="'basic'"
                [extensions]="lexQueryExtensions"
              ></code-editor>
            }
          </div>
        </div>
      </as-split-area>
    </as-split>
    <div class="configuration-taxonomy__editor-footer">
      @if (_canEditItem) {
        <div>
          <xi-default-button
            text="{{ t('cancel') }}"
            fill="text"
            buttonSize="large"
            [disabled]="isSaving || !hasChanges"
            (onClick)="cancelEdit()"
          ></xi-default-button>
          <xi-default-button
            text="{{ t('save') }}"
            buttonSize="large"
            (onClick)="saveEdit()"
            [disabled]="!hasChanges || !isValid || isSaving"
          ></xi-default-button>
        </div>
      }
    </div>
  }

  <ng-template #tTargetSort>
    <mat-list class="configuration-taxonomy__sort-menu">
      @for (option of targetSortValues; track option) {
        <mat-list-item (click)="onSortTargetClicked(option)">
          <mat-icon matListIcon> {{ option === targetSort ? 'check' : '' }} </mat-icon>
          <span matLine> {{ option.label }} </span>
        </mat-list-item>
      }
    </mat-list>
  </ng-template>

  <ng-template #tTargetKind>
    <mat-list class="configuration-taxonomy__search-kind-menu">
      <mat-list-item class="configuration-taxonomy__search-kind-menu-header">
        {{ t('listing') }}
      </mat-list-item>
      @for (option of targetSearchKindValues; track option) {
        <mat-list-item
          (click)="toggleTargetKindSelection(option)"
          [class.configuration-taxonomy__search-kind-menu--disabled]="option.disabled"
          [matTooltip]="getTargetTypeTooltip(option)"
        >
          <div matLine class="configuration-taxonomy__search-kind-content">
            <div>
              <div
                class="configuration-taxonomy__target-type"
                [ngClass]="'configuration-taxonomy__target-type--' + option.value"
              >
                {{ option.shortLabel }}
              </div>
              {{ option.label }}
            </div>
            @if (option === targetSearchKind) {
              <xi-icon icon="checkmark_18"></xi-icon>
            }
          </div>
        </mat-list-item>
      }
    </mat-list>
  </ng-template>
</ng-container>
